import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'

/**
 * A fee deducted from the gross CPM an advertiser pays. Mirrors `Fee` in core:
 *   - MONETARY is a fixed amount per 1000 impressions, in one currency.
 *   - MULTI_CURRENCY_MONETARY is that same fixed amount, quoted in several
 *     currencies; the one matching the bid's currency applies.
 *   - PERCENTAGE is a share of the gross CPM.
 */
export type FeeType = 'MONETARY' | 'MULTI_CURRENCY_MONETARY' | 'PERCENTAGE'

export interface Fee {
  type: FeeType
  name?: string
  reference?: string
  monetary?: CurrencyAmount
  percentage?: number
  multiCurrencyMonetaryAmounts?: CurrencyAmount[]
}

/**
 * Where a fee on the breakdown came from. Derived fees are set by the platform
 * — the advertiser cannot change them — so the UI shows them read-only and
 * names them itself rather than trusting the server's internal label.
 */
export type FeeSource = 'CPM' | 'TECH' | 'AUDIENCE' | 'SUPPLEMENTARY' | 'CREATIVE_FORMAT'

/** A fee as shown on the breakdown: the fee itself, plus how to label it. */
export interface DisplayFee extends Fee {
  source: FeeSource
  /**
   * Set when the fee covers creatives that charge different format fees: the
   * breakdown then reads as a range, and the net CPM as a range too.
   */
  maxAmount?: number
}

export interface CpmBreakdown {
  grossCpm?: CurrencyAmount
  fees: DisplayFee[]
  /**
   * The bid that actually enters the auction: gross CPM less every fee. When
   * the format fee is a range this is the *lowest* net CPM, and `netCpmMax`
   * holds the highest.
   */
  netCpm?: CurrencyAmount
  netCpmMax?: number
  /** The fees add up to more than the gross CPM, leaving nothing to bid with. */
  feesExceedGross: boolean
}

function isMoney(value: CurrencyAmount | undefined): value is CurrencyAmount {
  return !!value && typeof value.amount === 'number' && !!value.currency
}

/**
 * The amount of a multi-currency fee that applies to `currency`, falling back
 * to the first quoted amount when the fee does not cover that currency.
 */
export function matchingAmount(
  amounts: CurrencyAmount[] | undefined,
  currency: string | undefined
): CurrencyAmount | undefined {
  if (!amounts?.length) return undefined
  return amounts.find((a) => a.currency === currency) ?? amounts[0]
}

/**
 * Resolves a multi-currency fee down to the single amount that applies to the
 * bid's currency, so the rest of the code only has to read `monetary`.
 */
export function resolveFeeCurrency<T extends Fee>(fee: T, currency: string | undefined): T {
  if (fee.type !== 'MULTI_CURRENCY_MONETARY') return fee
  const monetary = matchingAmount(fee.multiCurrencyMonetaryAmounts, currency)
  return monetary ? { ...fee, monetary } : fee
}

/**
 * The line item's own fees as the API takes them back. A fixed fee must be in
 * the bid's currency, so each one follows the line item's — a currency changed
 * on the form would otherwise fail validation. Everything else is kept, the
 * `reference` a team fee carries included.
 */
export function feesForSave(fees: Fee[], currency: string): Fee[] {
  return fees.map((fee) =>
    fee.type === 'MONETARY' && fee.monetary
      ? { ...fee, monetary: { amount: fee.monetary.amount, currency: currency || fee.monetary.currency } }
      : fee
  )
}

/** The fixed part of the fees, in the reference amount's currency. */
function totalMonetaryFees(fees: Fee[], reference: CurrencyAmount | undefined): number {
  const currency = reference?.currency
  return fees.reduce((sum, fee) => {
    if (fee.type === 'MONETARY' && isMoney(fee.monetary) && fee.monetary.currency === currency) {
      return sum + fee.monetary.amount
    }
    if (fee.type === 'MULTI_CURRENCY_MONETARY') {
      const match = fee.multiCurrencyMonetaryAmounts?.find((a) => a.currency === currency)
      return isMoney(match) ? sum + match.amount : sum
    }
    return sum
  }, 0)
}

/** The proportional part of the fees, as a percentage of the gross CPM. */
function totalPercentageFees(fees: Fee[]): number {
  return fees.reduce(
    (sum, fee) => (fee.type === 'PERCENTAGE' && Number.isFinite(fee.percentage) ? sum + fee.percentage! : sum),
    0
  )
}

/**
 * The net CPM — the bid that reaches the auction — for a gross CPM and the fees
 * charged against it. Monetary fees come off the top, percentage fees are taken
 * from the gross.
 *
 * A fee carrying a `maxAmount` (the creative format fee, when the line item's
 * creatives charge different amounts) produces a range: the returned amount is
 * the lowest net CPM and `maxAmount` on the result the highest.
 */
export function calculateNetCpm(
  fees: DisplayFee[],
  grossCpm: CurrencyAmount | undefined
): (CurrencyAmount & { maxAmount?: number }) | undefined {
  if (!isMoney(grossCpm)) return undefined

  const monetary = totalMonetaryFees(fees, grossCpm)
  const percentage = totalPercentageFees(fees)
  const base = grossCpm.amount - monetary - (grossCpm.amount * percentage) / 100

  // The range fee was counted at its low end above, so the *high* end of the
  // fee gives the *low* end of the net CPM.
  const ranged = fees.find((fee) => fee.maxAmount != null && fee.monetary)
  if (ranged) {
    return {
      currency: grossCpm.currency,
      amount: base - (ranged.maxAmount! - ranged.monetary!.amount),
      maxAmount: base,
    }
  }
  return { currency: grossCpm.currency, amount: base }
}

/**
 * The gross CPM an advertiser has to pay for a given net CPM to survive the
 * fees — used to turn a marketplace product's net price floor into the minimum
 * the advertiser can enter.
 */
export function deriveGrossTargetCpm(
  fees: Fee[],
  netCpm: CurrencyAmount | undefined
): CurrencyAmount | undefined {
  if (!isMoney(netCpm)) return undefined
  const monetary = totalMonetaryFees(fees, netCpm)
  const percentage = totalPercentageFees(fees)
  // Fees at or above 100% leave no gross CPM that clears the floor.
  if (percentage >= 100) return netCpm
  return {
    currency: netCpm.currency,
    amount: (100 * (netCpm.amount + monetary)) / (100 - percentage),
  }
}

/**
 * Collapses the per-creative format fees into the single line the breakdown
 * shows. Creatives whose fee is in another currency are ignored, and when the
 * amounts differ — or only some creatives carry a fee at all — the line becomes
 * a range from the cheapest (or zero) to the dearest.
 */
export function collapseFormatFees(
  formatFees: Fee[],
  numCreatives: number,
  currency: string | undefined
): Omit<DisplayFee, 'name'> | undefined {
  const amounts = formatFees
    .map((fee) => resolveFeeCurrency(fee, currency).monetary)
    .filter((m): m is CurrencyAmount => isMoney(m) && m.currency === currency)
  if (!amounts.length) return undefined

  const unique = [...new Set(amounts.map((m) => m.amount))].sort((a, b) => a - b)
  const feeCurrency = amounts[0].currency
  // Creatives without a format fee pull the bottom of the range down to zero.
  const allCharged = amounts.length === numCreatives
  if (unique.length === 1 && allCharged) {
    return { source: 'CREATIVE_FORMAT', type: 'MONETARY', monetary: { currency: feeCurrency, amount: unique[0] } }
  }
  return {
    source: 'CREATIVE_FORMAT',
    type: 'MONETARY',
    monetary: { currency: feeCurrency, amount: allCharged ? unique[0] : 0 },
    maxAmount: unique[unique.length - 1],
  }
}
